import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core'
import { StepperSelectionEvent } from '@angular/cdk/stepper'
import { MatStepper } from '@angular/material/stepper'
import { GbEventTicketGuestStepperViewController } from './gb-event-ticket-guest-stepper-view-controller.service'
import { FormGroup } from '@angular/forms'
import { FormUtils } from 'libs/shared/utils/helpers/src/lib/form-utils'
import { GbNotificationService } from 'libs/shared/utils/notification/src/lib/service/gb-notification.service'
import {
  BaseEventTicketEntity,
  EventTicketDialogResultEnum,
  EventTicketGuestStepperConfig,
  EventTicketGuestStepperPayload,
  EventTicketProfilePayload,
} from 'libs/shared/event-tickets/data-access/src/lib/+state/base-event-tickets.models'
import { BaseEventTicketsFacade } from 'libs/shared/event-tickets/data-access/src/lib/+state/base-event-tickets.facade'
import { GbEventTicketFormQuestionsService } from 'libs/shared/event-tickets/utils/src/lib/gb-event-ticket-form-questions.service'
import { GbCookieService } from 'libs/shared/utils/services/src/lib/gb-cookie/gb-cookie.service'

@Component({
  selector: 'gb-event-ticket-guest-stepper',
  templateUrl: './gb-event-ticket-guest-stepper.component.html',
  styleUrls: ['./gb-event-ticket-guest-stepper.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class GbEventTicketGuestStepperComponent implements OnInit {
  @ViewChild('stepper') matStepper!: MatStepper
  @Output() onSubmitEvent = new EventEmitter<EventTicketGuestStepperPayload>()
  @Output() onStaticStepsComplete = new EventEmitter<EventTicketProfilePayload>()
  @Input() set config(value: EventTicketGuestStepperConfig) {
    this._config = { ...this._config, ...value }
  }

  _config: EventTicketGuestStepperConfig = {
    title: '',
    buttonLabel: 'CONFIRM',
    nextButtonLabel: 'NEXT',
    skipLinkLabel: 'SHARED.EVENT_TICKETS.ASSIGN_TICKET.SKIP_LINK',
    shouldShowCancelButton: true,
  }
  questionsForm = new FormGroup({})
  guestForm = new FormGroup({})
  isSelfAssigned = false
  EventTicketDialogResultEnum = EventTicketDialogResultEnum

  selectedStepIndex$ = this.gbEventTicketGuestStepperViewController.selectedStepSubject$
  selectedEventTicket$ = this.baseEventTicketsFacade.selectedEventTicket$
  selectedEventTicketCustomFields$ = this.baseEventTicketsFacade.selectedEventTicketCustomFields$
  selectedEventTicketCustomValues$ = this.baseEventTicketsFacade.selectedEventTicketCustomValues$

  get config(): EventTicketGuestStepperConfig {
    return this._config
  }

  get isAdminOrVolunteer(): boolean {
    return this.gbCookieService.isAdminOrVolunteer()
  }

  constructor(
    private baseEventTicketsFacade: BaseEventTicketsFacade,
    private gbEventTicketGuestStepperViewController: GbEventTicketGuestStepperViewController,
    private gbEventTicketFormQuestionsService: GbEventTicketFormQuestionsService,
    private notificationService: GbNotificationService,
    private gbCookieService: GbCookieService,
  ) {}

  ngOnInit(): void {
    this.gbEventTicketGuestStepperViewController.showGuestForm()
    setTimeout(() => (this.gbEventTicketGuestStepperViewController.matStepper = this.matStepper), 0)
  }

  hasCustomFields(eventTicket: BaseEventTicketEntity | undefined): boolean {
    return !!eventTicket && eventTicket.ticket_package.custom_fields.length > 0
  }

  shouldShowStepper(eventTicket: BaseEventTicketEntity): boolean {
    return this.hasCustomFields(eventTicket) || !!this.config.showDynamicStep
  }

  onStepChange(event: StepperSelectionEvent): void {
    this.gbEventTicketGuestStepperViewController.selectedStepSubject$.next(event.selectedIndex)
  }

  isGuestStep(selectedStep: number | null): boolean {
    return selectedStep === 0
  }

  isDynamicStep(selectedStep: number | null): boolean {
    return !!this.config.showDynamicStep && selectedStep === 1
  }

  hasAdditionalSteps(eventTicket: BaseEventTicketEntity, selectedStep: number | null): boolean {
    const isGuestStepWithAdditionalSteps = this.isGuestStep(selectedStep) && this.shouldShowStepper(eventTicket)
    const isDynamicStepWithAdditionalSteps = this.isDynamicStep(selectedStep) && this.hasCustomFields(eventTicket)

    return !!(isGuestStepWithAdditionalSteps || isDynamicStepWithAdditionalSteps)
  }

  getButtonLabel(eventTicket: BaseEventTicketEntity, selectedStep: number | null): string {
    return this.hasAdditionalSteps(eventTicket, selectedStep) ? this.config.nextButtonLabel! : this.config.buttonLabel
  }

  shouldShowSkipText(selectedStep: number | null): boolean {
    return !this.isGuestStep(selectedStep) && !this.isSelfAssigned
  }

  scrollToFirstInvalidField(): void {
    const firstInvalidField = document.querySelector(
      'gb-event-ticket-guest-form .ng-invalid, gb-event-ticket-questions-form .gb-invalid',
    )
    firstInvalidField?.scrollIntoView({ behavior: 'smooth' })
  }

  isFormValid(selectedStep: number | null, form: FormGroup): boolean {
    if (form.disabled) return true
    return (this.isSelfAssigned && !this.isAdminOrVolunteer) || this.isGuestStep(selectedStep) ? form.valid : true
  }

  onSubmit(eventTicket: BaseEventTicketEntity, selectedStep: number | null) {
    if (!this.isFormReadyToSubmit(selectedStep)) return

    if (this.config.emitOnStepSubmit) {
      let payload: EventTicketGuestStepperPayload = {}

      if (this.isGuestStep(selectedStep)) {
        payload = this.createGuestStepPayload()
      } else if (!this.isDynamicStep(selectedStep)) {
        payload = this.createQuestionsStepPayload()
      }

      this.onSubmitEvent.next(payload)
    } else if (this.hasAdditionalSteps(eventTicket, selectedStep)) {
      this.gbEventTicketGuestStepperViewController.progressStepper()
    } else {
      this.onSubmitEvent.next(this.createStepperPayload())
    }
  }

  private isFormReadyToSubmit(selectedStep: number | null): boolean {
    if (this.isDynamicStep(selectedStep)) return true

    const currentForm = this.isGuestStep(selectedStep) ? this.guestForm : this.questionsForm

    if (!this.isFormValid(selectedStep, currentForm)) {
      FormUtils.markFieldsAsTouched(currentForm)
      this.notificationService.error('SHARED.ERROR.FORM_INVALID')
      this.scrollToFirstInvalidField()
      return false
    }
    return true
  }

  private createStepperPayload(): EventTicketGuestStepperPayload {
    return {
      ...this.createGuestStepPayload(),
      ...this.createQuestionsStepPayload(),
    }
  }

  private createGuestStepPayload(): EventTicketGuestStepperPayload {
    return {
      profile: {
        ...this.guestForm.getRawValue(),
        table_number:
          this.guestForm.get('table_number')?.value === 'null' ? null : this.guestForm.get('table_number')?.value,
      },
      isSelfAssigned: this.isSelfAssigned,
    }
  }

  private createQuestionsStepPayload(): EventTicketGuestStepperPayload {
    return {
      customValues: this.gbEventTicketFormQuestionsService.getFormattedCustomValues(this.questionsForm.value),
    }
  }
}
